import { Link, Outlet, useLocation } from 'react-router-dom'
import { ShoppingBag, CalendarDays, UserRound, LockKeyhole, CreditCard } from 'lucide-react'
import { useTenant } from '../../contexts/TenantContext'
import { useCart } from '../../contexts/CartContext'
import { useTheme } from '../../contexts/ThemeContext'
import { useRef } from 'react'
import { skinFor, brandStyle, readableOn } from '../../pages/customer/templates/shared'
import SiteEdits from '../site-editor/SiteEdits'
import CartBottomBar from '../CartBottomBar'
import CartDrawer from '../CartDrawer'
import SocialLinks from '../SocialLinks'
import { LanguageSwitcher } from '../../i18n'
import { t } from '../../i18n'
export default function CustomerLayout() {
 const tenant = useTenant()
 const { itemCount, openDrawer } = useCart()
 const { getAppName } = useTheme()
 const location = useLocation()
 const services = tenant.services || []
 const sells = services.includes('menu') || services.includes('store')
 const hasShop = services.includes('store')
 // Each service the store runs gets its own link; the first one is the home page.
 const sectionLinks = services.length > 1 ? services.map((id, i) => ({
  id, to: i === 0 ? '/' : ({ menu: '/menu', store: '/shop', bookings: '/book' })[id],
  label: ({ menu: 'Menu', store: 'Shop', bookings: 'Book an appointment' })[id]
 })) : []
 const name = tenant.name || getAppName()
 const customTheme = tenant.settings?.custom_theme?.active ? tenant.settings.custom_theme : null
 // An uploaded theme's colors win over the template's skin colors; without
 // one, the skin's own tokens (index.css) apply.
 const themeStyle = customTheme ? Object.fromEntries(Object.entries({
  '--theme-primary': customTheme.colors?.primary,
  '--theme-secondary': customTheme.colors?.secondary,
  '--theme-accent': customTheme.colors?.accent,
  '--theme-background': customTheme.colors?.background,
  '--theme-text': customTheme.colors?.text
 }).filter(([, v]) => v)) : brandStyle(tenant.settings)
 // Colors picked in the website editor win over everything else.
 const edits = tenant.settings?.site_edits
 const ec = edits?.colors || {}
 const editorColors = {
  ...(ec.primary && { '--theme-primary': ec.primary, '--brand': ec.primary, '--sk-on-primary': readableOn(ec.primary), '--brand-on': readableOn(ec.primary) }),
  ...(ec.accent && { '--theme-accent': ec.accent, '--brand-accent': ec.accent }),
  ...(ec.background && { '--theme-background': ec.background }),
  ...(ec.text && { '--theme-text': ec.text }),
 }
 const siteRef = useRef(null)
 const social = tenant.settings?.social_links
 const subscriptionLocked = tenant.subscriptionLocked && !inEditor()
 const query = location.search
 if (location.pathname === '/' && !tenant.loading && !tenant.found) return <Outlet />
 return <div className="customer-site relative min-h-screen" data-skin={skinFor(tenant)} style={{ ...themeStyle, ...editorColors }} data-uploaded-theme={customTheme?.id} ref={siteRef}>
  {subscriptionLocked && (
   <div className="fixed inset-0 z-[100] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-md" role="dialog" aria-modal="true" aria-labelledby="subscription-lock-title">
    <div className="w-full max-w-md rounded-3xl border border-white/70 bg-white/95 p-6 text-center shadow-2xl sm:p-8">
     <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-blue-50 text-blue-700"><LockKeyhole className="h-7 w-7" /></span>
     <h1 id="subscription-lock-title" className="mt-5 text-2xl font-bold text-slate-950">{t('Pay your subscription to unlock this website')}</h1>
     <p className="mt-3 leading-7 text-slate-600">{t('This store is not accepting visits or orders until its subscription is activated.')}</p>
     <Link to="/admin/billing" className="mt-6 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-700 px-5 py-3 font-semibold text-white transition-colors hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
      <CreditCard className="h-5 w-5" />{t('Pay subscription')}
     </Link>
    </div>
   </div>
  )}
  <div className={subscriptionLocked ? 'pointer-events-none select-none blur-sm' : ''} aria-hidden={subscriptionLocked || undefined}>
  <SiteEdits edits={edits} rootRef={siteRef} />
  <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:start-2 focus:z-50 bg-white p-3">{t("Skip to content")}</a>
  <header className="store-header"><div className="store-header-inner"><Link to={`/${query}`} className="store-brand">{(customTheme?.logoUrl || tenant.logoUrl) ? <img src={customTheme?.logoUrl || tenant.logoUrl} alt=""/> : <span className="store-monogram">{name.charAt(0)}</span>}<span>{name}</span></Link>
    <nav aria-label={t("Main navigation")} className="flex items-center gap-2 sm:gap-5">
     <div className="hidden lg:block"><SocialLinks links={social} size={16} /></div>
     <LanguageSwitcher />
     {sectionLinks.length === 0 && sells && <Link to={`/${query}`} className="hidden sm:block text-sm">{hasShop ? t("Collection") : t("Menu")}</Link>}
     {services.includes('menu') && tenant.settings?.reservations_enabled !== false && <Link to={`/reservations${query}`} className="store-nav-action"><CalendarDays size={18}/><span className="hidden sm:inline">{t("Reserve a table")}</span></Link>}
     <Link aria-label={t("Your account")} to={`/account${query}`} className="store-nav-action"><UserRound size={19}/></Link>
     {sells && (hasShop ? (
       <button aria-label={t("Open cart")} onClick={openDrawer} className="store-nav-action"><ShoppingBag size={19}/><span>({itemCount})</span></button>
     ) : (
       <Link to={`/cart${query}`} className="store-nav-action"><ShoppingBag size={19}/><span>({itemCount})</span></Link>
     ))}
    </nav></div>
    {sectionLinks.length > 0 && (
     <nav aria-label={t("Store sections")} className="store-sections">
      {sectionLinks.map(link => {
       const current = location.pathname === link.to || (link.to === '/' && location.pathname === '/')
       return <Link key={link.id} to={`${link.to}${query}`} aria-current={current ? 'page' : undefined} className={current ? 'is-current' : ''}>{t(link.label)}</Link>
      })}
     </nav>
    )}
  </header>
  <main id="main-content" className="store-main"><Outlet /></main>
  <footer className="store-footer">
   <div><Link to={`/${query}`} className="font-semibold">{name}</Link><p className="mt-1">© {new Date().getFullYear()} {name}</p></div>
   <SocialLinks links={social} />
   {sells && <Link to={`/order-status${query}`}>{t("Track an order")}</Link>}
  </footer>
  <CartBottomBar />
  {hasShop && <CartDrawer />}
  </div>
 </div>
}
